import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { catchError, EMPTY, filter, Subscription, take, timeout } from 'rxjs'
import { AssistantClientToolsService } from '../../services/assistant-client-tools.service'
import { AssistantConversationService } from '../../services/assistant-conversation.service'

/**
 * The entitled (Cnect Plus) composer that lives INSIDE the persistent bottom bar. Instantiated only in
 * the command bar's entitled branch, so a Default/loading session never constructs it — no conversation,
 * no listener, no client-tool executor, no POST (the story-4.3 zero-backend-calls guarantee, preserved
 * across the bottom-bar refactor).
 *
 * Sending a message opens the dock so the reply is visible. `⌘K` / `Ctrl-K` focuses the input from
 * anywhere (the "always one keystroke away" behavior) without hijacking any other focused field. The
 * conversation is created lazily — on first focus, or on the first send if that races ahead — so users
 * who never engage the assistant don't spawn an empty conversation on every page load.
 */
@Component({
  selector: 'assistant-bar-active',
  template: `
    <div class="e11-flex e11-flex-col e11-gap-1">
      <p *ngIf="sendError" class="e11-text-xs e11-text-skin-error e11-mb-0 e11-px-2">
        {{ 'Could not send your message — try again.' | translate }}
      </p>
      <div
        class="e11-flex e11-items-center e11-gap-2 e11-bg-skin-white e11-pl-4 e11-pr-2 e11-py-2 focus-within:e11-ring-2 focus-within:e11-ring-skin-primary-accent"
        [ngClass]="fullWidth ? 'e11-rounded-lg e11-border e11-border-skin-app-borders' : 'e11-rounded-full e11-shadow-lg'"
      >
        <span class="material-icons-outlined md-24 e11-text-skin-primary-accent">auto_awesome</span>
        <input
          #input
          type="text"
          [(ngModel)]="draft"
          [disabled]="sending"
          (focus)="onFocus()"
          (keydown.enter)="onEnter($event)"
          [attr.aria-label]="'Ask or tell the assistant anything…' | translate"
          [placeholder]="'Ask or tell the assistant anything…' | translate"
          class="e11-flex-1 e11-min-w-0 e11-border-none e11-bg-transparent e11-text-sm focus:e11-outline-none disabled:e11-text-skin-light"
        />
        <span
          class="e11-hidden xl:e11-inline e11-text-xs e11-text-skin-light e11-border e11-border-skin-app-borders e11-rounded e11-px-1.5 e11-py-0.5"
          >⌘K</span
        >
        <e11-button
          [color]="'primary'"
          [value]="''"
          symbol="send"
          [titleText]="'Send' | translate"
          [attr.aria-label]="'Send' | translate"
          [disabled]="(working$ | async) || !draft.trim() || sending"
          (click)="send()"
        ></e11-button>
      </div>
    </div>
  `,
})
export class AssistantBarActiveComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('input') inputEl?: ElementRef<HTMLInputElement>

  /** Full-width rectangular input (terminal view) vs the default rounded pill (bar view). */
  @Input() fullWidth = false

  /** True while a run is in progress; gates the send button (CAP-3 — 409/busy never shown, prevented). */
  working$ = this.assistantService.working$

  draft = ''
  sendError = false
  /** In-flight guard: prevents a double-send in the idle window before the backend flips status. */
  sending = false
  /** Live mirror of `working$` so the Enter path can refuse a send while the assistant is busy (the
   *  send button is already disabled; this stops a keyboard send from POSTing into a raced 409). */
  private busy = false

  private sub = new Subscription()

  constructor(private assistantService: AssistantConversationService, private clientTools: AssistantClientToolsService) {}

  /**
   * Connect the client-tool executor to the live `steps$` stream (story 4.9). It's a no-op until a
   * conversation exists, so connecting on mount is safe and lets the assistant act (e.g. navigate) from
   * a bar command even before the dock is opened. The conversation itself is created lazily (`onFocus`).
   */
  ngOnInit(): void {
    this.sub.add(this.clientTools.connect())
    this.sub.add(this.working$.subscribe(b => (this.busy = b)))
  }

  /** The composer only mounts when the bar is expanded (a user action), so focusing on view-init lands
   *  the cursor in the input the moment it opens — no extra click after ⌘K or the launcher. */
  ngAfterViewInit(): void {
    this.inputEl?.nativeElement.focus()
  }

  /** ⌘K / Ctrl-K focuses the bar from anywhere; never steals keystrokes from another focused field. */
  @HostListener('document:keydown', ['$event'])
  onGlobalKey(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault()
      this.inputEl?.nativeElement.focus()
    }
  }

  /** Create the conversation on first intent to use it, not on every page load. Idempotent. */
  onFocus(): void {
    this.assistantService.ensureConversation()
  }

  onEnter(event: Event): void {
    const keyboard = event as KeyboardEvent
    if (keyboard.shiftKey) return
    event.preventDefault()
    this.send()
  }

  /**
   * Send, robust to the lazy-create race: `sendMessage` needs a conversation id, which may still be in
   * flight on the very first send. We `ensureConversation()` then wait for the first non-empty
   * `activeConversationId$` before POSTing; if one already exists it emits immediately. A create that
   * never resolves (swallowed backend error) times out and surfaces the inline "try again" rather than
   * hanging the composer forever.
   */
  send(): void {
    const text = this.draft.trim()
    if (!text || this.sending || this.busy) return
    this.sendError = false
    this.sending = true
    this.assistantService.ensureConversation()
    // Clear optimistically; restore the text if the send fails so "try again" doesn't force a retype.
    this.draft = ''
    this.sub.add(
      this.assistantService.activeConversationId$
        .pipe(
          filter((id): id is string => !!id),
          take(1),
          timeout(8000),
          catchError(() => {
            this.failSend(text)
            return EMPTY
          })
        )
        .subscribe(() => {
          this.sub.add(
            this.assistantService.sendMessage(text).subscribe({
              error: () => this.failSend(text),
              complete: () => (this.sending = false),
            })
          )
        })
    )
  }

  private failSend(text: string): void {
    this.sendError = true
    this.draft = text
    this.sending = false
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe()
  }
}
